import {AsyncInlineError, inlineError} from '@s-ui/decorators'

import {FileAttachmentEntity} from '../Entities/FileAttachmentEntity.js'
import {MessagingConversationIdEntity} from '../Entities/MessagingConversationIdEntity.js'
import {CheckUserEmailExistsUserError} from '../Errors/CheckUserEmailExistsUserError.js'
import {CreateWhatsappIntentsUserError} from '../Errors/CreateWhatsappIntentsUserError.js'
import {GetBlockedUsersError} from '../Errors/GetBlockedUsersError.js'
import GetBuyerIntentionsUserError from '../Errors/GetBuyerIntentionsUserError.js'
import {GetMessagingChannelContextUserError} from '../Errors/GetMessagingChannelContextUserError.js'
import {GetMessagingConversationIdUserError} from '../Errors/GetMessagingConversationIdUserError.js'
import {NotFoundUserError} from '../Errors/NotFoundUserError.js'
import {UnauthorizedUserError} from '../Errors/UnauthorizedUserError.js'
import {UnblockUserError} from '../Errors/UnblockUserError.js'
import {UpdatePhoneUserError} from '../Errors/UpdatePhoneUserError.js'
import {UpdateUserError} from '../Errors/UpdateUserError.js'
import {UploadAttachmentUserError} from '../Errors/UploadAttachmentUserError.js'
import {UploadAvatarUserError} from '../Errors/UploadAvatarUserError.js'
import {BlockedUsersValueObject} from '../ValueObjects/BlockedUsersValueObject.js'
import BuyerIntentionsListUserValueObject from '../ValueObjects/BuyerIntentionsListUserValueObject.js'
import ContactResponseV2ValueObject from '../ValueObjects/ContactResponseV2ValueObject.js'
import {GetMessagingAdsListUserValueObject} from '../ValueObjects/GetMessagingAdsListUserValueObject.js'
import {GetMessagingProviderTokenUserValueObject} from '../ValueObjects/GetMessagingProviderTokenUserValueObject.js'
import {MessagingChannelContextValueObject} from '../ValueObjects/MessagingChannelContextValueObject.js'
import {UpdatePasswordResponseUserValueObject} from '../ValueObjects/UpdatePasswordResponseUserValueObject.js'
import {UploadAvatarResponseUserValueObject} from '../ValueObjects/UploadAvatarResponseUserValueObject.js'
import {UserEmailExistsValueObject} from '../ValueObjects/UserEmailExistsValueObject.js'
import {WhatsappIntentsUserValueObject} from '../ValueObjects/WhatsappIntentsUserValueObject.js'
import UserRepository from './UserRepository.js'

/**
 * @class
 * @implements UserRepository
 */
export default class HTTPUserRepository extends UserRepository {
  constructor({
    adShareByEmailUserErrorFactory,
    apiWebUrl,
    authenticationFetcher,
    checkGigyaUserExistsUserErrorFactory,
    config,
    fetcher,
    fetchOrFail,
    fromUserConsentsStatusUserValueObjectToHttpUpdateConsentsRequestBodyMapper,
    fromHttpGetConsentsResponseToUserConsentsStatusUserValueObjectMapper,
    fromRawDataToGetConversationEntityUserMapper,
    fromSubscriptionsRawDataToSpecificSubscriptionsMapper,
    fromUserRawDataToUserEntityMapper,
    genericUserError,
    getConversationUserErrorFactory,
    hTTPUserRepositoryToContactResponseValueObjectMapper,
    hTTPUserTakeOutListToUserTakeOutListValueObjectMapper,
    leadHitTrackingValueObjectFactory,
    leadHitUserErrorFactory,
    log,
    notFoundUserError,
    phoneVerificationStatusVOFactory,
    saveAdShareByEmailHitUserErrorFactory,
    securedApiUrl,
    sendEmailToVerifyUserError,
    subscriptionsUserValueObjectFactory,
    unauthorizedUserError,
    updateCurrentUserRequestFactory,
    userEntityFactory,
    userExistsValueObjectFactory,
    userNotLoggedInUserErrorFactory,
    userTakeoutLinkValueObjectFactory,
    validatePhoneVerificationCodeUserErrorFactory,
    httpResponseToSessionAuthenticationMapper
  }) {
    super()
    this._adShareByEmailUserErrorFactory = adShareByEmailUserErrorFactory
    this._apiWebUrl = apiWebUrl
    this._authenticationFetcher = authenticationFetcher
    this._config = config
    this._detailPageName = this._config.get('page').ADS_DETAIL_PAGE_NAME
    this._postFavoriteContactOrigin = this._config.get('contact').CONTACT_ORIGINS.POST_FAVORITE
    this._fetcher = fetcher
    this._fetchOrFail = fetchOrFail
    this._fromUserConsentsStatusUserValueObjectToHttpUpdateConsentsRequestBodyMapper =
      fromUserConsentsStatusUserValueObjectToHttpUpdateConsentsRequestBodyMapper
    this._fromHttpGetConsentsResponseToUserConsentsStatusUserValueObjectMapper =
      fromHttpGetConsentsResponseToUserConsentsStatusUserValueObjectMapper
    this._fromUserRawDataToUserEntityMapper = fromUserRawDataToUserEntityMapper
    this._genericUserError = genericUserError
    this._hTTPUserRepositoryToContactResponseValueObjectMapper = hTTPUserRepositoryToContactResponseValueObjectMapper
    this._hTTPUserTakeOutListToUserTakeOutListValueObjectMapper = hTTPUserTakeOutListToUserTakeOutListValueObjectMapper
    this._leadHitTrackingValueObjectFactory = leadHitTrackingValueObjectFactory
    this._leadHitUserErrorFactory = leadHitUserErrorFactory
    this._log = log
    this._notFoundUserError = notFoundUserError
    this._saveAdShareByEmailHitUserErrorFactory = saveAdShareByEmailHitUserErrorFactory
    this._securedApiUrl = securedApiUrl
    this._vehicleConfiguratorPageName = this._config.get('page').VEHICLE_CONFIGURATOR_PAGE_NAME
    this._stockPageName = this._config.get('page').PROFESSIONAL_STOCK_PAGE_NAME
    this._stockRentingPageName = this._config.get('page').PROFESSIONAL_STOCK_RENTING_PAGE_NAME
    this._userFavoritesPageName = this._config.get('page').USER_FAVORITE_ADS_PAGE_NAME
    this._unauthorizedUserError = unauthorizedUserError
    this._userTakeoutLinkValueObjectFactory = userTakeoutLinkValueObjectFactory
    this._checkGigyaUserExistsUserErrorFactory = checkGigyaUserExistsUserErrorFactory
    this._userExistsValueObjectFactory = userExistsValueObjectFactory
    this._getConversationUserErrorFactory = getConversationUserErrorFactory
    this._phoneVerificationStatusVOFactory = phoneVerificationStatusVOFactory
    this._fromRawDataToGetConversationEntityMapper = fromRawDataToGetConversationEntityUserMapper
    this._fromSubscriptionsRawDataToSpecificSubscriptionsMapper = fromSubscriptionsRawDataToSpecificSubscriptionsMapper
    this._sendEmailToVerifyUserError = sendEmailToVerifyUserError
    this._subscriptionsUserValueObjectFactory = subscriptionsUserValueObjectFactory
    this._updateCurrentUserRequestFactory = updateCurrentUserRequestFactory
    this._userEntityFactory = userEntityFactory
    this._userNotLoggedInUserErrorFactory = userNotLoggedInUserErrorFactory
    this._validatePhoneVerificationCodeUserErrorFactory = validatePhoneVerificationCodeUserErrorFactory
    this._httpResponseToSessionAuthenticationMapper = httpResponseToSessionAuthenticationMapper

    this._getRequestHeaders = ({captchaToken, contactToken}) => {
      const {CAPTCHA_TOKEN, CONTACT_TOKEN} = this._config.get('REQUEST_HEADERS')
      return {
        [CAPTCHA_TOKEN]: captchaToken,
        [CONTACT_TOKEN]: contactToken
      }
    }
  }

  @inlineError
  async getPhoneVerificationCode({userEntity}) {
    const userId = userEntity.getId()
    const {email, phone} = userEntity.toJSON()

    const endpoint = `${this._config.get('API_URL')}/users/${userId}/phone/verification?phone=${phone}&email=${email}`

    return this._authenticationFetcher
      .post(endpoint)
      .then(() => Promise.resolve())
      .catch(error => {
        throw this._validatePhoneVerificationCodeUserErrorFactory({
          code: error.response?.data?.code,
          remainingAttempts: error.response?.data?.remainingAttempts
        })
      })
  }

  @inlineError
  async getUser({requestVO}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {userId} = requestVO.toJSON()

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._getDealerErrorFactory('Dealer information can not be fetched')
      })
      .get(`${this._config.get('API_URL')}/private-seller/${userId}`)

    if (error) return Promise.reject(error)

    const {data} = response
    return this._fromUserRawDataToUserEntityMapper.map(data)
  }

  /**
   * @return {Array.<Error,UserConsentsStatusUserValueObject>}
   */
  @inlineError
  async getConsents({userEntity}) {
    if (!userEntity)
      throw this._userNotLoggedInUserErrorFactory({
        message: 'User Not Logged In Error'
      })

    const userId = userEntity.getId()
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {NOT_FOUND, UNAUTHORIZED} = this._config.get('COMMON').HTTP_STATUS_CODES

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => {
          return this._genericUserError.create({
            message: 'Generic User Consents Error'
          })
        },
        [NOT_FOUND]: () => {
          return this._notFoundUserError.create({
            message: 'User Not Found Consents Error'
          })
        },
        [UNAUTHORIZED]: () => {
          return this._unauthorizedUserError.create({
            message: 'Unauthorized User Consents Error'
          })
        }
      })
      .get(`${this._apiWebUrl}/v2/users/${userId}/consents`)

    if (error) throw error

    const {data} = response

    return this._fromHttpGetConsentsResponseToUserConsentsStatusUserValueObjectMapper.map(data)
  }

  /**
   * @return {Promise<UserTakeoutListValueObject, Error>}
   */
  getUserTakeoutList({userEntity, useTranscendAPI = false}) {
    this._log('Getting user takeout list from repository')

    const userId = userEntity.getId()
    const method = useTranscendAPI ? 'get' : 'post'

    return this._authenticationFetcher[method](`${this._apiWebUrl}/users/${userId}/takeout`).then(({data}) =>
      this._hTTPUserTakeOutListToUserTakeOutListValueObjectMapper.map(data)
    )
  }

  /**
   * @return {Promise<UserTakeoutListValueObject, Error>}
   */
  getUserTakeoutLink({filename, requestId, userEntity, useTranscendAPI = false}) {
    this._log('Getting user takeout link from repository')

    const userId = userEntity.getId()

    if (useTranscendAPI) {
      // New behavior: download binary directly with authentication
      return this._authenticationFetcher
        .get(`${this._apiWebUrl}/users/${userId}/requests/${requestId}/takeout/download`, {
          responseType: 'blob'
        })
        .then(({data}) => this._userTakeoutLinkValueObjectFactory({blob: data}))
    } else {
      return this._authenticationFetcher
        .post(`${this._apiWebUrl}/users/${userId}/takeout/${requestId}/files/${filename}/url`)
        .then(({data}) => this._userTakeoutLinkValueObjectFactory(data))
    }
  }

  _getContactByMailAsLoggedUserEndpointUrl({context, contractId, adId}) {
    switch (context) {
      case this._detailPageName:
        return `${this._apiWebUrl}/v2/vehicles/${adId}/contact-mail`
      case this._vehicleConfiguratorPageName:
        return `${this._securedApiUrl}/dealers/${contractId}/contact-mail?origin=vehicle-configurator`
      case this._stockPageName:
      case this._stockRentingPageName:
        return `${this._securedApiUrl}/dealers/${contractId}/contact-mail`
      case this._userFavoritesPageName:
        if (adId) return `${this._apiWebUrl}/v2/vehicles/${adId}/contact-mail`
        else return `${this._securedApiUrl}/dealers/${contractId}/contact-mail`
      default:
        return `${this._apiWebUrl}/v2/vehicles/${adId}/contact-mail`
    }
  }

  _getContactByMailAsNotLoggedUserEndpointUrl({context, contractId, adId}) {
    switch (context) {
      case this._detailPageName:
        return `${this._apiWebUrl}/v2/vehicles/${adId}/contact-mail-not-logged`
      case this._stockPageName:
      case this._stockRentingPageName:
        return `${this._securedApiUrl}/dealers/${contractId}/contact-mail-not-logged`
      case this._userFavoritesPageName:
        if (adId) return `${this._apiWebUrl}/v2/vehicles/${adId}/contact-mail-not-logged`
        else return `${this._securedApiUrl}/dealers/${contractId}/contact-mail-not-logged`
      default:
        return `${this._apiWebUrl}/v2/vehicles/${adId}/contact-mail-not-logged`
    }
  }

  @inlineError
  contactByMailAsLoggedUser({
    adId,
    captchaToken,
    contactToken,
    context,
    contractId,
    message,
    buyer,
    enrichment,
    preferences,
    isPostFavoriteLead
  }) {
    this._log('Contacting by mail as logged user')

    const headers = this._getRequestHeaders({captchaToken, contactToken})
    const endpointUrl = this._getContactByMailAsLoggedUserEndpointUrl({
      context,
      contractId,
      adId
    })

    return this._authenticationFetcher
      .post(
        endpointUrl,
        {
          buyer,
          message,
          enrichment,
          ...(preferences && {preferences}),
          ...(isPostFavoriteLead && {origin: this._postFavoriteContactOrigin}),
          mobile: this._config.get('isMobile')
        },
        {headers}
      )
      .then(response => ContactResponseV2ValueObject.create({response, config: this._config}))
      .catch(error => Promise.reject(ContactResponseV2ValueObject.create({response: error, config: this._config})))
  }

  @inlineError
  contactByMailAsNotLoggedUser({
    adId,
    captchaToken,
    contactToken,
    context,
    contractId,
    message,
    buyer,
    enrichment,
    preferences
  }) {
    this._log('Contacting by mail as not logged user V2')

    const headers = this._getRequestHeaders({captchaToken, contactToken})

    const endpointUrl = this._getContactByMailAsNotLoggedUserEndpointUrl({
      context,
      contractId,
      adId
    })

    return this._fetcher
      .post(
        endpointUrl,
        {
          buyer,
          message,
          enrichment,
          ...(preferences && {preferences}),
          mobile: this._config.get('isMobile')
        },
        {headers}
      )
      .then(response => ContactResponseV2ValueObject.create({response, config: this._config}))
      .catch(error => Promise.reject(ContactResponseV2ValueObject.create({response: error, config: this._config})))
  }

  @inlineError
  async saveLeadHit(leadHitUserRequest) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {endpointUrl} = leadHitUserRequest.toJSON()

    if (!endpointUrl) throw this._leadHitUserErrorFactory('Hit endpoint url not valid')

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => {
          return this._leadHitUserErrorFactory('Lead hit can not be saved')
        }
      })
      .post(endpointUrl)

    if (error) throw error

    const {
      data: {hitId}
    } = response

    return this._leadHitTrackingValueObjectFactory({hitId})
  }

  @inlineError
  async saveAdShareByEmailHit({adId}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const endpoint = `${this._config.get('SECURED_API_URL')}/hits/vehicles/${adId}/recommend`

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message}) => {
          return this._saveAdShareByEmailHitUserErrorFactory(message)
        }
      })
      .post(endpoint)

    if (error) throw error
    return Promise.resolve()
  }

  @inlineError
  async adShareByEmail({adId, contractId, email}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {BAD_REQUEST} = this._config.get('COMMON').HTTP_RESPONSE_CODES
    const endpoint = `${this._config.get('SECURED_API_URL')}/recommend`
    const body = {adId, contractId, mail: email}

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message}) => {
          return this._adShareByEmailUserErrorFactory(message)
        }
      })
      .post(endpoint, body)

    if (error) return Promise.reject(error)

    if (response.data === BAD_REQUEST)
      return Promise.reject(this._adShareByEmailUserErrorFactory('Unable to share this ad'))

    return Promise.resolve()
  }

  @inlineError
  async checkGigyaUserExists({gigyaUID}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const endpoint = `${this._config.get('API_URL')}/users/${gigyaUID}/check`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message}) => {
          return this._checkGigyaUserExistsUserErrorFactory({message})
        }
      })
      .get(endpoint)

    if (error) return Promise.reject(error)

    return this._userExistsValueObjectFactory({
      userExists: response.data.exists
    })
  }

  @inlineError
  async checkPhoneVerificationStatus({userEntity}) {
    const userId = userEntity.getId()
    const {phone} = userEntity.toJSON()

    const endpoint = `${this._config.get('API_URL')}/users/${userId}/phone/is-verified?phone=${phone}`

    return this._authenticationFetcher
      .get(endpoint)
      .then(({data}) => this._phoneVerificationStatusVOFactory({isVerified: data}))
  }

  @inlineError
  async getSubscriptions({subscriptionsParams}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {subscriptionType, token, userId} = subscriptionsParams.toJSON()
    const endpoint = `${this._config.get('API_URL')}/subscriptions/${userId}?token=${token}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: message => this._genericUserError.create({message})
      })
      .get(endpoint)

    if (error) throw error
    const {data} = response
    return this._fromSubscriptionsRawDataToSpecificSubscriptionsMapper.map({data: {...data, type: subscriptionType}})
  }

  @inlineError
  async getUserById({currentUser}) {
    const userId = await currentUser.getId()
    const endpoint = `${this._config.get('API_URL')}/users/${userId}`

    return this._authenticationFetcher.get(endpoint).then(({data}) => {
      const {
        active,
        avatarUrl: avatar,
        creationDate,
        email,
        hidePhone,
        id: userId,
        lastName,
        name,
        origin,
        phone,
        province,
        provinceId,
        verified,
        zipCode
      } = data

      return this._userEntityFactory({
        active,
        avatarUrl: avatar,
        creationDate,
        email,
        hidePhone,
        id: userId,
        isEmailConfirmed: verified,
        lastName,
        name,
        origin,
        phone,
        province,
        provinceId,
        verified,
        zipCode
      })
    })
  }

  @inlineError
  async sendEmailToVerifyUser({currentUser, redirectUri}) {
    const userId = await currentUser.getId()

    const queryParams = redirectUri ? `?redirectUri=${encodeURIComponent(redirectUri)}` : ''
    const endpoint = `${this._config.get('API_URL')}/users/${userId}/send-verification${queryParams}`

    return this._authenticationFetcher.post(endpoint).then(res => {
      return res.status !== 200
        ? this._sendEmailToVerifyUserError(res.statusText)
        : Promise.resolve('Email succesfully sent')
    })
  }

  /**
   * @return {Array.<Error,UserConsentsStatusUserValueObject>}
   */
  @inlineError
  async updateConsents({userConsentsStatusUserValueObject, userEntity}) {
    if (!userEntity)
      throw this._userNotLoggedInUserErrorFactory({
        message: 'User Not Logged In Error'
      })

    const userId = userEntity.getId()

    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {NOT_FOUND, UNAUTHORIZED} = this._config.get('COMMON').HTTP_STATUS_CODES

    const requestBody = this._fromUserConsentsStatusUserValueObjectToHttpUpdateConsentsRequestBodyMapper.map(
      userConsentsStatusUserValueObject
    )

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => {
          return this._genericUserError.create({
            message: 'Generic User Consents Error'
          })
        },
        [NOT_FOUND]: () => {
          return this._notFoundUserError.create({
            message: 'User Not Found Consents Error'
          })
        },
        [UNAUTHORIZED]: () => {
          return this._unauthorizedUserError.create({
            message: 'Unauthorized User Consents Error'
          })
        }
      })
      .put(`${this._apiWebUrl}/users/${userId}/consents`, requestBody)

    if (error) throw error
  }

  @inlineError
  async updateSubscriptions({subscriptionsParams}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {newSubscriptions, token, userId} = subscriptionsParams.toJSON()
    const endpoint = `${this._config.get('API_URL')}/subscriptions/${userId}?token=${token}`
    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: message => this._genericUserError.create({message})
      })
      .post(endpoint, newSubscriptions)
    if (error) throw error
    const {data} = response

    return this._subscriptionsUserValueObjectFactory({email: data.email, subscriptions: data.subscriptions})
  }

  @inlineError
  async updateUserData({updateDataUserRequest, useNewEndpoint, userEntity}) {
    const {CONFLICT} = this._config.get('COMMON').HTTP_STATUS_CODES

    const userId = userEntity.getId()

    const endpoint = useNewEndpoint
      ? `${this._config.get('API_URL')}/users/${userId}`
      : `${this._config.get('API_URL')}/user/${userId}`

    return this._authenticationFetcher
      .put(endpoint, updateDataUserRequest.toJSON())
      .then(response =>
        useNewEndpoint
          ? this._httpResponseToSessionAuthenticationMapper.map(response.data)
          : this._updateCurrentUserRequestFactory(response.data)
      )
      .catch(error => {
        if (error.status === CONFLICT) throw UpdatePhoneUserError.create({message: error.response?.data})

        throw UpdateUserError.create({
          code: error.response?.data?.code || UpdateUserError.GENERIC_UPDATE_USER_ERROR_CODE
        })
      })
  }

  @inlineError
  async updateUserPassword({updatePasswordUserRequest, userEntity}) {
    const userId = userEntity.getId()

    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {UNAUTHORIZED} = this._config.get('COMMON').HTTP_STATUS_CODES

    const endpoint = `${this._config.get('API_URL')}/users/${userId}/password`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => {
          return this._genericUserError.create({
            message: 'Generic Update User Password Error'
          })
        },
        [UNAUTHORIZED]: () => {
          return this._unauthorizedUserError.create({
            message: 'Unauthorized Update User Password Error'
          })
        }
      })
      .put(endpoint, updatePasswordUserRequest.toJSON())

    if (error) throw error

    const {data} = response

    return UpdatePasswordResponseUserValueObject.create(data)
  }

  @inlineError
  async validatePhoneVerificationCode({userEntity, validatePhoneVerificationCodeUserRequest}) {
    const userId = userEntity.getId()
    const {code, phone} = validatePhoneVerificationCodeUserRequest.toJSON()

    const endpoint = `${this._config.get(
      'API_URL'
    )}/users/${userId}/phone/verification/complete?phone=${phone}&code=${code}`

    return this._authenticationFetcher
      .post(endpoint)
      .then(() => Promise.resolve())
      .catch(error => {
        throw this._validatePhoneVerificationCodeUserErrorFactory({
          code: error.response?.data?.code,
          remainingAttempts: error.response?.data?.remainingAttempts
        })
      })
  }

  @AsyncInlineError()
  async uploadAvatar({image, userEntity}) {
    const AVATAR_MAX_SIZE = 5242880 // 5MB
    if (image?.size > AVATAR_MAX_SIZE)
      return Promise.reject(UploadAvatarUserError.create({message: 'File size exceeds the limit of 5MB'}))

    const userId = userEntity.getId()
    const url = `${this._config.get('API_URL')}/users/${userId}/avatar`

    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {NOT_FOUND, UNAUTHORIZED, FORBIDDEN} = this._config.get('COMMON').HTTP_STATUS_CODES

    const formData = new FormData()
    formData.append('file', image)

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => UploadAvatarUserError.create({message: 'Generic Upload Avatar Error'}),
        [FORBIDDEN]: () => UploadAvatarUserError.create({message: 'Forbidden User Upload Avatar Error'}),
        [NOT_FOUND]: () => NotFoundUserError.create({message: 'Not Found User Upload Avatar Error'}),
        [UNAUTHORIZED]: () => UnauthorizedUserError.create({message: 'Unauthorized User Upload Avatar Error'})
      })
      .post(url, formData)

    if (error) return Promise.reject(error)

    return Promise.resolve(UploadAvatarResponseUserValueObject.create(response.data))
  }

  @AsyncInlineError()
  async deleteUserAccount({userEntity}) {
    const {userId} = userEntity.toJSON()

    try {
      await this._authenticationFetcher.delete(`${this._config.get('API_URL')}/users/${userId}`)
    } catch (error) {
      return Promise.reject(error)
    }
  }

  @AsyncInlineError()
  async requestTakeoutData({userEntity}) {
    const userId = userEntity.getId()
    const apiUrl = this._config.get('API_URL')

    try {
      await this._authenticationFetcher.post(`${apiUrl}/users/${userId}/takeout`)
    } catch (error) {
      return Promise.reject(error)
    }
  }

  @AsyncInlineError()
  async getMessagingProviderTokenUserUseCase({userEntity}) {
    const userId = userEntity.getId()
    const url = `${this._config.get('API_URL')}/users/${userId}/messagingToken`

    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {NOT_FOUND, UNAUTHORIZED, FORBIDDEN} = this._config.get('COMMON').HTTP_STATUS_CODES

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => UploadAvatarUserError.create({message: 'Generic Get Messaing Provider token Error'}),
        [FORBIDDEN]: () => UploadAvatarUserError.create({message: 'Forbidden User Get Messaing Provider token Error'}),
        [NOT_FOUND]: () => NotFoundUserError.create({message: 'Not Found User Get Messaing Provider token Error'}),
        [UNAUTHORIZED]: () =>
          UnauthorizedUserError.create({message: 'Unauthorized User Get Messaing Provider token Error'})
      })
      .post(url)

    if (error) return Promise.reject(error)

    return Promise.resolve(GetMessagingProviderTokenUserValueObject.create({token: response?.data?.token || ''}))
  }

  @AsyncInlineError()
  async getMessagingAdsList({request, adIds}) {
    const url = `${this._config.get('API_URL')}/messaging/ad_list?${request.toQueryParams()}`

    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {NOT_FOUND, UNAUTHORIZED, FORBIDDEN} = this._config.get('COMMON').HTTP_STATUS_CODES

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => UploadAvatarUserError.create({message: 'Generic Get Messaging Ads List Error'}),
        [FORBIDDEN]: () => UploadAvatarUserError.create({message: 'Forbidden User Get Messaging Ads List Error'}),
        [NOT_FOUND]: () => NotFoundUserError.create({message: 'Not Found User Get Messaging Ads List Error'}),
        [UNAUTHORIZED]: () => UnauthorizedUserError.create({message: 'Unauthorized User Get Messaging Ads List Error'})
      })
      .get(url)
    if (error) return Promise.reject(error)

    return Promise.resolve(GetMessagingAdsListUserValueObject.create({ads: response?.data?.ads || []}))
  }

  @AsyncInlineError()
  async getMessagingConversationId({messagingConversationIdUserRequest}) {
    const {NOT_FOUND, UNAUTHORIZED} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const data = messagingConversationIdUserRequest.toJSON()
    const query = new URLSearchParams(data).toString()

    const url = `${this._config.get('API_URL')}/contacts/stream-channel-id?${query}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () =>
          GetMessagingConversationIdUserError.create({message: 'Generic Get Messaging Conversation ID Error'}),
        [NOT_FOUND]: () => NotFoundUserError.create({message: 'Not Found User Get Messaging Conversation ID Error'}),
        [UNAUTHORIZED]: () =>
          UnauthorizedUserError.create({message: 'Unauthorized User Get Messaging Conversation ID Error'})
      })
      .get(url)

    if (error) return Promise.reject(error)

    const channelId = response?.data?.channelId
    if (!channelId) {
      return Promise.reject(GetMessagingConversationIdUserError.create({message: 'Missing channelId in response'}))
    }

    return MessagingConversationIdEntity.create({conversationId: channelId})
  }

  @AsyncInlineError()
  async uploadAttachment({uploadAttachmentUserRequest}) {
    const {NOT_FOUND, UNAUTHORIZED} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const {channelId, file} = uploadAttachmentUserRequest.toJSON()
    const isImage = file.type.startsWith('image/')

    const endpoint = `${this._config.get('API_URL')}/v1/channels/${channelId}/${isImage ? 'image' : 'file'}`

    const formData = new FormData()
    formData.append('file', file)

    const body = formData
    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => UploadAttachmentUserError.create({message: 'Generic Upload Error'}),
        [NOT_FOUND]: () => NotFoundUserError.create({message: 'Not Found Upload Error'}),
        [UNAUTHORIZED]: () => UnauthorizedUserError.create({message: 'Unauthorized Upload Error'})
      })
      .post(endpoint, body)

    if (error) return Promise.reject(error)

    return Promise.resolve(FileAttachmentEntity.create({url: response?.data?.url}))
  }

  @AsyncInlineError()
  async getMessagingChannelContext({getMessagingChannelContextUserRequest}) {
    const {NOT_FOUND, UNAUTHORIZED} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const {channelId} = getMessagingChannelContextUserRequest.toJSON()

    const url = `${this._config.get('API_URL')}/contacts/stream-channel-context?channelId=${channelId}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => GetMessagingChannelContextUserError.create({message: 'Generic Channel Context Error'}),
        [NOT_FOUND]: () => NotFoundUserError.create({message: 'Not Found User Error'}),
        [UNAUTHORIZED]: () => UnauthorizedUserError.create({message: 'Unauthorized User Error'})
      })
      .get(url)

    if (error) return Promise.reject(error)

    return Promise.resolve(MessagingChannelContextValueObject.create({...response?.data}))
  }

  @AsyncInlineError()
  async getBlockedUsers({userEntity}) {
    const {NOT_FOUND, UNAUTHORIZED} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const userId = userEntity.getId()
    const url = `${this._config.get('API_URL')}/users/${userId}/blocked`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => GetBlockedUsersError.create({message: 'Generic Get Blocked Users Error'}),
        [NOT_FOUND]: () => NotFoundUserError.create({message: 'Not Found User Error'}),
        [UNAUTHORIZED]: () => UnauthorizedUserError.create({message: 'Unauthorized User Error'})
      })
      .get(url)

    if (error) return Promise.reject(error)

    return BlockedUsersValueObject.create({...response?.data})
  }

  @AsyncInlineError()
  async unblockUser({userEntity, userToUnblockId}) {
    const {NOT_FOUND, UNAUTHORIZED} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const userId = userEntity.getId()
    const url = `${this._config.get('API_URL')}/users/${userId}/unblock/${userToUnblockId}`

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => UnblockUserError.create({message: 'Generic Unblock User Error'}),
        [NOT_FOUND]: () => NotFoundUserError.create({message: 'Not Found User Error'}),
        [UNAUTHORIZED]: () => UnauthorizedUserError.create({message: 'Unauthorized User Error'})
      })
      .post(url)

    if (error) throw error
    return Promise.resolve()
  }

  /**
   * Get buyer intentions list
   * @returns {Promise<BuyerIntentionsListUserValueObject>}
   */
  @AsyncInlineError()
  async getBuyerIntentions() {
    const endpoint = `${this._config.get('API_URL')}/contacts/v1/buyer-intentions`

    const [error, response] = await this._fetchOrFail.get(endpoint)

    if (error) {
      throw GetBuyerIntentionsUserError.create(error)
    }

    const {data} = response
    return BuyerIntentionsListUserValueObject.create(data)
  }

  /**
   * Check if a user with the given email already exists
   * @param {Object} params
   * @param {string} params.email
   * @returns {Promise<UserExistsValueObject>}
   */
  @AsyncInlineError()
  async checkUserEmailExists({email}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const endpoint = `${this._config.get('API_URL')}/users/user-email-exists?email=${encodeURIComponent(email)}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message}) => new CheckUserEmailExistsUserError({message})
      })
      .get(endpoint)

    if (error) return Promise.reject(error)

    return UserEmailExistsValueObject.create({exists: response.data.exists})
  }

  @AsyncInlineError()
  async getDefaultConsents() {
    const endpoint = `${this._config.get('API_URL')}/users/consents/default`

    const [error, response] = await this._fetchOrFail.get(endpoint)

    if (error) return Promise.reject(error)

    return response.data.consents
  }

  @AsyncInlineError()
  async createWhatsappIntents({createWhatsappIntentsUserRequest, userEntity}) {
    const {NOT_FOUND, UNAUTHORIZED} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const userId = userEntity.getId()
    const endpoint = `${this._config.get('API_URL')}/contacts/users/${userId}/whatsapp-intents`
    const body = createWhatsappIntentsUserRequest.toJSON()

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => CreateWhatsappIntentsUserError.create({message: 'Generic Create Whatsapp Intents Error'}),
        [NOT_FOUND]: () => NotFoundUserError.create({message: 'Not Found Create Whatsapp Intents Error'}),
        [UNAUTHORIZED]: () => UnauthorizedUserError.create({message: 'Unauthorized Create Whatsapp Intents Error'})
      })
      .post(endpoint, body)

    if (error) return Promise.reject(error)

    return WhatsappIntentsUserValueObject.create({
      chatDeepLink: response?.data?.chatDeepLink,
      id: response?.data?.id
    })
  }

  @AsyncInlineError()
  async createWhatsappIntentsAsNotLoggedUser({createWhatsappIntentsUserRequest}) {
    const endpoint = `${this._config.get('API_URL')}/contacts/whatsapp-intents`
    const body = createWhatsappIntentsUserRequest.toJSON()

    return this._fetcher
      .post(endpoint, body)
      .then(response =>
        WhatsappIntentsUserValueObject.create({
          chatDeepLink: response?.data?.chatDeepLink,
          id: response?.data?.id
        })
      )
      .catch(() =>
        Promise.reject(CreateWhatsappIntentsUserError.create({message: 'Generic Create Whatsapp Intents Error'}))
      )
  }
}
